import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'

/** Aç/kapa durumu: `[opened, { open, close, toggle }]`. */
export function useDisclosure(initial = false) {
  const [opened, setOpened] = useState(initial)
  const open = useCallback(() => setOpened(true), [])
  const close = useCallback(() => setOpened(false), [])
  const toggle = useCallback(() => setOpened((o) => !o), [])
  return [opened, { open, close, toggle }] as const
}

/** Değerin gecikmeli kopyası (arama kutusu). */
export function useDebouncedValue<T>(value: T, wait: number): [T] {
  const [v, setV] = useState(value)
  useEffect(() => {
    const h = setTimeout(() => setV(value), wait)
    return () => clearTimeout(h)
  }, [value, wait])
  return [v]
}

/** localStorage'a bağlı durum; erişim engellenirse (gizli pencere) bellekte çalışır. */
export function useLocalStorage<T>({ key, defaultValue }: { key: string; defaultValue: T }) {
  const read = (): T => {
    try {
      const raw = localStorage.getItem(key)
      return raw === null ? defaultValue : (JSON.parse(raw) as T)
    } catch {
      return defaultValue
    }
  }
  const [value, setValue] = useState<T>(read)
  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v
      try { localStorage.setItem(key, JSON.stringify(next)) } catch { /* yok say */ }
      return next
    })
  }, [key])
  return [value, set] as const
}

export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false)
  const [match, setMatch] = useState(get)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const h = () => setMatch(mq.matches)
    h()
    mq.addEventListener('change', h)
    return () => mq.removeEventListener('change', h)
  }, [query])
  return match
}

/** Klavye kısayolu: `useHotkey('mod+K', fn)` — mod = macOS'ta ⌘, diğerlerinde Ctrl. */
export function useHotkey(combo: string, handler: (e: KeyboardEvent) => void) {
  const ref = useRef(handler)
  ref.current = handler
  useEffect(() => {
    const parts = combo.toLowerCase().split('+')
    const key = parts[parts.length - 1]
    const mod = parts.includes('mod')
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== key) return
      if (mod && !(isMac() ? e.metaKey : e.ctrlKey)) return
      e.preventDefault()
      ref.current(e)
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [combo])
}

export function isMac(): boolean {
  return typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent)
}

/** Öğenin boyutu (ResizeObserver). */
export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setSize({ width: e.contentRect.width, height: e.contentRect.height }))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { ref, ...size }
}

export type ColorScheme = 'light' | 'dark'
export type ColorSchemePref = ColorScheme | 'auto'
const SCHEME_KEY = 'nx-color-scheme'
/** Mantine döneminin anahtarı: kayıtlı tercih kaybolmasın diye bir kez okunur. */
const OLD_SCHEME_KEY = 'mantine-color-scheme-value'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function readPref(): ColorSchemePref {
  try {
    const v = localStorage.getItem(SCHEME_KEY) ?? localStorage.getItem(OLD_SCHEME_KEY)
    return v === 'light' || v === 'dark' ? v : 'auto'
  } catch { return 'auto' }
}

/* Tek paylaşılan durum: başlıktaki düğme ile kökteki eşitleyici aynı tercihi görür. */
let currentPref: ColorSchemePref = readPref()
const schemeListeners = new Set<() => void>()
const emitScheme = () => schemeListeners.forEach((l) => l())

function subscribeScheme(cb: () => void) {
  schemeListeners.add(cb)
  // "Sistem" seçiliyken işletim sistemi teması değişirse uygulama da değişsin.
  const mq = window.matchMedia?.(DARK_QUERY)
  mq?.addEventListener?.('change', cb)
  return () => { schemeListeners.delete(cb); mq?.removeEventListener?.('change', cb) }
}

function systemScheme(): ColorScheme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light'
}

/**
 * Tema tercihi (açık / koyu / sistem). Kökteki data-theme özniteliğini yazar;
 * index.html'deki ilk boyama betiği aynı anahtarı okuyup React'ten önce uygular.
 */
export function useColorScheme() {
  const pref = useSyncExternalStore(subscribeScheme, () => currentPref, () => currentPref)
  // Sistem teması abonelikle tetiklenir; değeri her çizimde okunur.
  const system = useSyncExternalStore(subscribeScheme, systemScheme, () => 'light' as ColorScheme)
  const scheme: ColorScheme = pref === 'auto' ? system : pref
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', scheme)
  }, [scheme])
  const set = (p: ColorSchemePref) => {
    try { localStorage.setItem(SCHEME_KEY, p) } catch { /* yok say */ }
    currentPref = p
    emitScheme()
  }
  return { scheme, pref, setPref: set, toggle: () => set(scheme === 'dark' ? 'light' : 'dark') }
}
